import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { ClientDto, DeviceDto, Page, SiteDto, UserDto } from '@zenarty/shared';
import { api } from '../api';
import { useToast } from '../components/toast';
import { errorText } from '../components/kit';

export const useSites = (enabled = true) =>
  useQuery({ queryKey: ['sites'], queryFn: () => api<Page<SiteDto>>('/sites?limit=200'), enabled });
export const useClients = (enabled = true) =>
  useQuery({
    queryKey: ['clients'],
    queryFn: () => api<Page<ClientDto>>('/clients?limit=200'),
    enabled,
  });
export const useUsers = (enabled = true) =>
  useQuery({ queryKey: ['users'], queryFn: () => api<Page<UserDto>>('/users?limit=200'), enabled });
export const useDevices = (enabled = true) =>
  useQuery({
    queryKey: ['devices'],
    queryFn: () => api<Page<DeviceDto>>('/devices?limit=200'),
    enabled,
  });

/** Mutation that refreshes the given lists and the dashboard, with toasts. */
export function useAction<TVars, TResult = unknown>(
  fn: (vars: TVars) => Promise<TResult>,
  opts: {
    invalidate: QueryKey[];
    success?: string | ((r: TResult) => string);
    onSuccess?: (r: TResult) => void;
  },
) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (result) => {
      await Promise.all(
        [...opts.invalidate, ['dashboard'], ['audit']].map((key) =>
          qc.invalidateQueries({ queryKey: key }),
        ),
      );
      if (opts.success)
        toast.ok(typeof opts.success === 'function' ? opts.success(result) : opts.success);
      opts.onSuccess?.(result);
    },
    onError: (err) => toast.error(errorText(err)),
  });
}

export function nameMap<T extends { id: string }>(items: T[] | undefined, label: (t: T) => string) {
  return new Map((items ?? []).map((i) => [i.id, label(i)]));
}

/** Server-paged list with search and filters (large operational tables). */
export function useServerList<T>(
  key: string,
  path: string,
  filters: Record<string, string | undefined>,
  pageSize = 50,
) {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const filterKey = JSON.stringify(filters);
  useEffect(() => setPage(0), [filterKey, search]);
  const params = new URLSearchParams({ limit: String(pageSize), offset: String(page * pageSize) });
  for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
  if (search) params.set('q', search);
  const qs = params.toString();
  const query = useQuery({
    queryKey: [key, 'list', qs],
    queryFn: () => api<Page<T>>(`${path}?${qs}`),
    placeholderData: keepPreviousData,
  });
  return {
    query,
    server: {
      total: query.data?.total ?? 0,
      page,
      pageSize,
      onPage: setPage,
      search,
      onSearch: setSearch,
    },
  };
}
